import { createContext, useState, useEffect } from "react";
import { axiosPrivate } from "../api/axios";
import logger from "../utils/logger";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    // Initialize from localStorage if persist is enabled
    const token = localStorage.getItem("accessToken");
    const storedAuth = localStorage.getItem("auth");

    if (token && storedAuth) {
      try {
        const parsed = JSON.parse(storedAuth);
        return parsed;
      } catch (e) {
        logger.error("[AuthProvider] Failed to parse stored auth:", e);
        return {};
      }
    }

    if (token) {
      return { accessToken: token };
    }

    return {};
  });
  const [persist, setPersist] = useState(
    JSON.parse(localStorage.getItem("persist")) || false,
  );

  // Initialize employee from cached auth so profile (name, avatar) shows immediately
  const [employee, setEmployee] = useState(() => {
    try {
      const storedAuth = localStorage.getItem("auth");
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        if (parsed?.employee && typeof parsed.employee === "object") {
          return parsed.employee;
        }
      }
    } catch (e) {
      // ignore
    }
    return null;
  });
  const [isLoadingEmployee, setIsLoadingEmployee] = useState(false);

  // Sync employee when auth is set with employee (e.g. after login or change-password)
  useEffect(() => {
    if (auth?.employee && auth.employee !== employee) {
      setEmployee(auth.employee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.employee]);

  // Fetch full profile in background when token exists (refreshes photo, nickname, etc.)
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token && auth?.accessToken) {
      fetchEmployee();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.accessToken]);

  const fetchEmployee = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    setIsLoadingEmployee(true);
    try {
      const response = await axiosPrivate.get("/api/employees/me");
      setEmployee(response.data);
      // Update auth with employee data
      setAuth((prev) => ({ ...prev, employee: response.data }));
    } catch (err) {
      logger.error("Failed to fetch employee:", err);
      // If 401, clear auth
      if (err?.response?.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("auth");
        setAuth({});
        setEmployee(null);
      }
    } finally {
      setIsLoadingEmployee(false);
    }
  };

  // Update localStorage when auth changes
  useEffect(() => {
    if (auth?.accessToken) {
      localStorage.setItem("accessToken", auth.accessToken);
      localStorage.setItem("auth", JSON.stringify(auth));
    }
  }, [auth]);

  useEffect(() => {
    localStorage.setItem("persist", JSON.stringify(persist));
  }, [persist]);

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth,
        persist,
        setPersist,
        employee,
        setEmployee,
        fetchEmployee,
        isLoadingEmployee,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
